/**
 * Priority Relationships scoring — implements the rubric documented in
 * `docs/my-portfolio/priority-relationships-scoring-design.md` §4 (Priority
 * Score Anatomy). That doc is the authoritative formula; the mock
 * (`my-accounts-portfolio-v4.html`) has NO real scoring function to port —
 * its "Prioritize for Me" view is static hardcoded demo data (`PFM_PAIRS`,
 * JS lines ~7086-7097) revealed by a display-toggle, not computed. This is
 * a genuine, from-the-doc implementation, not a mock port.
 *
 * `RelationshipPairRow` (from `my-portfolio/overview.relationship_pairs`)
 * does not carry every field the doc's rubric calls for. Components/inputs
 * that are NOT computable from available data are documented inline and
 * scored as 0 (contribute nothing) rather than guessed:
 *   - §4.4 team RL lag (no team-average RL field) — omitted.
 *   - §4.5 role-type modifier (no `role_type` field, only `contact_title`
 *     free text) — only the seniority-tier base score is computed.
 *   - §4.6 AL Stagnation (10 pts) — AL goal/level is account-scoped, not
 *     present on the pair row — omitted entirely (0 pts).
 *   - §4.8 Signal Confidence — no active-input-vs-passive-signal
 *     distinction is tracked; `assessed_days_ago` is used as a staleness
 *     proxy against the doc's "passive signal" bands only.
 *   - §4.9 ARR Multiplier — no ARR field on the pair row — fixed at 1.0.
 * Achievable max with these gaps is ~85 (30 + 25 + 20 + 10), not the doc's
 * full 100 — tier thresholds below are calibrated to that reduced scale.
 */

import type { PortfolioClusterKey } from "./portfolioClusterStyles";
import type { RelationshipPairRow } from "../../Store/Reducers/MyPortfolioOverviewSlice";

export type PriorityTier = "vh" | "h" | "m" | "l";

export const TIER_LABEL: Record<PriorityTier, string> = {
  vh: "Very High",
  h: "High",
  m: "Medium",
  l: "Low",
};

/** mock `.prio-tier-*` colors, JS `PFM_TIER_COLOR` (line ~7099). */
export const TIER_COLOR: Record<PriorityTier, string> = {
  vh: "#dc2626",
  h: "#d97706",
  m: "#9333EA",
  l: "#9ca3af",
};

export type PriorityQuadrant = "dofirst" | "build" | "fasttrack" | "maintain";

/** Tenant-configurable multipliers on each scoring category below (Portfolio
 * Settings → Priority Scoring Weights). Defaults of 1.0 reproduce the exact
 * point values this module always computed — a weight only changes the
 * relative pull of that category on the final score. `tierFromScore`'s
 * band cutoffs stay fixed rather than rescaling with the achievable max;
 * an aggressive weight change can shift how many pairs land in each tier,
 * which is the intended effect of raising a weight. */
export interface PriorityScoringWeights {
  riskWeight: number;
  relationshipGapWeight: number;
  seniorityWeight: number;
  signalConfidenceWeight: number;
}

export const DEFAULT_PRIORITY_SCORING_WEIGHTS: PriorityScoringWeights = {
  riskWeight: 1,
  relationshipGapWeight: 1,
  seniorityWeight: 1,
  signalConfidenceWeight: 1,
};

export interface ScoredPair {
  pair: RelationshipPairRow;
  /** null = unassessed pair, not yet scored (doc §4.10). */
  score: number | null;
  tier: PriorityTier | "unassessed";
  /** 0-1, matrix X axis ("URGENCY →"). */
  urgency: number;
  /** 0-1, matrix Y axis ("IMPORTANCE ↑"). */
  importance: number;
  quadrant: PriorityQuadrant | null;
}

/** Doc §4.3 — Account Risk / Opportunity cluster base weight. */
const CLUSTER_RISK_PTS: Record<PortfolioClusterKey, number> = {
  triage: 28,
  defend: 20,
  grow: 12,
  expand: 6,
  unscored: 0,
};

function clusterKey(raw: string | null): PortfolioClusterKey {
  const k = (raw || "").toLowerCase();
  if (k === "triage" || k === "defend" || k === "grow" || k === "expand") {
    return k;
  }
  return "unscored";
}

/** Doc §4.3 — renewal proximity (exponential urgency). */
function renewalPts(days: number | null): number {
  if (days == null) return 0;
  if (days < 30) return 30;
  if (days < 90) return 24;
  if (days < 180) return 16;
  return 8;
}

/** Doc §4.4 + §4.7 — RL gap from goal, or Goals-Configuration-Gap fallback
 * when no individual RL goal is set (team-goal-missing addend omitted, no
 * team-goal field available). */
function relationshipGapPts(pair: RelationshipPairRow): number {
  if (pair.rl_goal == null) return 15; // §4.7 individual RL goal missing
  const gap =
    pair.rl_gap != null ? pair.rl_gap : pair.rl_goal - (pair.rl_current ?? 0);
  if (gap <= 0) return 0;
  if (gap === 1) return 8;
  if (gap === 2) return 16;
  return 22;
}

/** Doc §4.5 — exec seniority tier from free-text title (role-type modifier
 * omitted — not present in `RelationshipPairRow`). */
function seniorityPts(title: string | null): number {
  const t = (title || "").toLowerCase();
  const cSuite = [
    "ceo",
    "cfo",
    "cto",
    "cpo",
    "ciso",
    "coo",
    "cmo",
    "cro",
    "president",
    "chief ",
  ];
  if (cSuite.some(k => t.includes(k))) return 20;
  if (t.includes("evp") || t.includes("svp")) return 16;
  if (t.includes(" vp") || t.startsWith("vp") || t.includes("vice president"))
    return 11;
  if (t.includes("director")) return 6;
  return 0;
}

/** Doc §4.8 — recency × confidence, passive-signal bands only (no
 * active-input tracking available). Uses assessed_days_ago as the best
 * available staleness proxy, falling back to mapped_days_ago. */
function signalConfidencePts(pair: RelationshipPairRow): number {
  const days = pair.assessed_days_ago ?? pair.mapped_days_ago;
  if (days == null) return 10;
  if (days < 30) return 5;
  if (days <= 60) return 8;
  return 10;
}

function tierFromScore(score: number): PriorityTier {
  if (score >= 70) return "vh";
  if (score >= 50) return "h";
  if (score >= 30) return "m";
  return "l";
}

function quadrantFromAxes(
  urgency: number,
  importance: number
): PriorityQuadrant {
  const highU = urgency >= 0.5;
  const highI = importance >= 0.5;
  if (highU && highI) return "dofirst";
  if (!highU && highI) return "build";
  if (highU && !highI) return "fasttrack";
  return "maintain";
}

/** Score one pair per the components above. Returns null score/tier
 * "unassessed" for mapped-but-not-yet-RL-assessed pairs (doc §4.10). */
export function scorePair(
  pair: RelationshipPairRow,
  weights: PriorityScoringWeights = DEFAULT_PRIORITY_SCORING_WEIGHTS
): ScoredPair {
  if (pair.rl_current == null) {
    return {
      pair,
      score: null,
      tier: "unassessed",
      urgency: 0,
      importance: 0,
      quadrant: null,
    };
  }

  const risk =
    Math.max(
      CLUSTER_RISK_PTS[clusterKey(pair.account_cluster)],
      renewalPts(pair.account_renewal_days)
    ) * weights.riskWeight;
  const relGap =
    Math.min(relationshipGapPts(pair), 25) * weights.relationshipGapWeight;
  const leverage =
    Math.min(seniorityPts(pair.contact_title), 25) * weights.seniorityWeight;
  const signal = signalConfidencePts(pair) * weights.signalConfidenceWeight;

  const score = Math.round(risk + relGap + leverage + signal);
  const tier = tierFromScore(score);

  // Matrix axes: urgency ~= risk/signal urgency-weighted; importance ~=
  // strategic leverage + relationship-gap weighted. Normalized to the
  // achievable maxima (risk 30, signal 10; leverage 20, relGap 25) so both
  // axes span 0-1 the same way the mock's hand-authored 0-1 values do.
  const urgency = Math.min(1, (risk + signal) / 40);
  const importance = Math.min(1, (leverage + relGap) / 45);

  return {
    pair,
    score,
    tier,
    urgency,
    importance,
    quadrant: quadrantFromAxes(urgency, importance),
  };
}

export function scorePairs(
  pairs: RelationshipPairRow[],
  weights: PriorityScoringWeights = DEFAULT_PRIORITY_SCORING_WEIGHTS
): ScoredPair[] {
  return pairs.map(pair => scorePair(pair, weights));
}

export const TIER_ORDER: PriorityTier[] = ["vh", "h", "m", "l"];

export const QUADRANT_LABEL: Record<PriorityQuadrant, string> = {
  build: "BUILD",
  dofirst: "DO FIRST",
  maintain: "MAINTAIN",
  fasttrack: "FAST-TRACK",
};

/** mock JS `PFM_QUAD_FILL` (line ~7100-7105). */
export const QUADRANT_FILL: Record<PriorityQuadrant, string> = {
  dofirst: "rgba(220,38,38,0.07)",
  build: "rgba(37,99,235,0.06)",
  fasttrack: "rgba(217,119,6,0.07)",
  maintain: "rgba(156,163,175,0.07)",
};

/** mock JS quadrant label fill colors (line ~7131 inline ternary). */
export const QUADRANT_LABEL_COLOR: Record<PriorityQuadrant, string> = {
  dofirst: "#dc2626",
  build: "#2563eb",
  fasttrack: "#d97706",
  maintain: "#9ca3af",
};
